/**
 * Пометка «делист» / «стоп» у конкретной пары НА КОНКРЕТНОЙ бирже.
 *
 * Данные — из статусов инструментов самих бирж (бэкенд, `delistings.py`):
 * у перпа назначена дата поставки, у спота закрыты торги и т.п. Показываем
 * рядом с названием площадки, потому что снимают пару именно ТАМ: спред может
 * быть настоящим, но забрать его не выйдет — вывод монеты закрывают заранее.
 *
 * Данные общие для всей страницы, поэтому держим их в модульном кэше: каждая
 * строка сканера иначе делала бы свой запрос.
 */
import React, { useEffect, useState } from 'react';
import clsx from 'clsx';
import { apiClient } from '../api/client';
import { useTranslation } from 'react-i18next';

export interface DelistMark { kind: 'delist' | 'halt'; ts: number | null; raw: string }
type Venues = Record<string, Record<string, DelistMark>>;

const REFRESH_MS = 5 * 60_000;
let cache: Venues = {};
let cacheTs = 0;
let inflight: Promise<void> | null = null;
const listeners = new Set<(v: Venues) => void>();

async function load(): Promise<void> {
  if (inflight) return inflight;
  inflight = (async () => {
    try {
      const r = await apiClient.raw.get('/api/delistings');
      cache = r.data?.venues || {};
      cacheTs = Date.now();
      listeners.forEach(fn => fn(cache));
    } catch { /* не авторизованы / бэк не отвечает — просто без пометок */ }
    finally { inflight = null; }
  })();
  return inflight;
}

/** Подписка на общий кэш пометок. */
export function useDelistings(): Venues {
  const [v, setV] = useState<Venues>(cache);
  useEffect(() => {
    listeners.add(setV);
    if (!cacheTs || Date.now() - cacheTs > REFRESH_MS) void load();
    const id = window.setInterval(() => { void load(); }, REFRESH_MS);
    return () => { listeners.delete(setV); window.clearInterval(id); };
  }, []);
  return v;
}

export function delistMark(venues: Venues, ex: string, symbol: string): DelistMark | null {
  const m = venues?.[ex]?.[(symbol || '').toUpperCase()];
  return m || null;
}

const fmtDate = (ts: number | null) =>
  ts ? new Date(ts).toLocaleDateString('ru-RU', { day: '2-digit', month: '2-digit', year: 'numeric' }) : null;

/** Компактная плашка. `ex` — площадка, `symbol` — канонический тикер строки. */
export const DelistBadge: React.FC<{ ex: string; symbol: string; venues?: Venues }> = ({ ex, symbol, venues }) => {
  const { t } = useTranslation('delistBadge');
  const all = venues ?? cache;
  const m = delistMark(all, ex, symbol);
  if (!m) return null;
  const when = fmtDate(m.ts);
  const isDelist = m.kind === 'delist';
  const title = isDelist
    ? (when ? t('title.delistWhen', { when, ex, raw: m.raw }) : t('title.delist', { ex, raw: m.raw }))
    : t('title.halt', { ex, raw: m.raw });
  return (
    <span
      title={title}
      className={clsx('rounded px-1 py-0.5 text-[11px] uppercase tracking-wider whitespace-nowrap',
        isDelist ? 'bg-trade-red/20 text-trade-red' : 'bg-primary/15 text-primary')}
    >
      {isDelist ? (when ? t('badge.delistWhen', { date: when.slice(0, 5) }) : t('badge.delist')) : t('badge.halt')}
    </span>
  );
};

export default DelistBadge;
